import { useState } from 'react'
import { useScheduledTasks, useCreateScheduledTask, useUpdateScheduledTask, useDeleteScheduledTask, useToggleScheduledTask } from '@/api/scheduled-tasks'
import { useFleets, usePlaybooks, useDevices } from '@/api/devices'
import { RequirePermission } from '@/components/RequirePermission'
import type { ScheduledTask, TargetType, CreateScheduledTaskRequest } from '@/types'

interface Props {
  tenantId: string | null
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function cronHuman(expr: string): string {
  try {
    const parts = expr.trim().split(/\s+/)
    if (parts.length !== 5) return expr
    const [min, hour, dom, month, dow] = parts
    if (min === '0' && dom === '*' && month === '*' && dow === '*') {
      return `Daily at ${hour.padStart(2, '0')}:00`
    }
    if (dom === '*' && month === '*' && dow !== '*') {
      const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
      const d = parseInt(dow)
      return `Every ${isNaN(d) ? dow : days[d]} at ${hour.padStart(2, '0')}:${min.padStart(2, '0')}`
    }
    if (min === '*/15' || min === '*/30') {
      return `Every ${min.replace('*/', '')} min`
    }
    if (min === '*' && hour === '*') return 'Every minute'
    return expr
  } catch {
    return expr
  }
}

function statusBadge(status: string | undefined) {
  if (!status) return null
  const map: Record<string, string> = {
    success: 'badge-active',
    failed: 'badge-failed',
    running: 'badge-provisioning',
  }
  return <span className={`badge ${map[status] ?? 'badge-pending'}`}>{status}</span>
}

const inputStyle: React.CSSProperties = {
  background: 'var(--color-surface)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius)',
  padding: '6px 10px',
  color: 'var(--color-text)',
  fontSize: 13,
  width: '100%',
}

// ── Create / Edit modal ───────────────────────────────────────────────────────

interface TaskFormProps {
  tenantId: string
  task?: ScheduledTask
  fleets: Array<{ id: string; name: string }>
  devices: Array<{ id: string; name: string }>
  playbooks: Array<{ name: string }>
  onClose: () => void
}

function TaskFormModal({ tenantId, task, fleets, devices, playbooks, onClose }: TaskFormProps) {
  const isEdit = !!task
  const [name, setName] = useState(task?.name ?? '')
  const [description, setDescription] = useState(task?.description ?? '')
  const [playbook, setPlaybook] = useState(task?.playbook ?? '')
  const [schedule, setSchedule] = useState(task?.schedule ?? '0 3 * * *')
  const [targetType, setTargetType] = useState<TargetType>(task?.target_type ?? 'fleet')
  const [targetId, setTargetId] = useState(task?.target_id ?? '')
  const [awxLimit, setAwxLimit] = useState(task?.awx_limit ?? '')

  const create = useCreateScheduledTask(tenantId)
  const update = useUpdateScheduledTask(tenantId, task?.id ?? '')

  const isPending = create.isPending || update.isPending

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!name || !playbook || !schedule || !targetId) return

    if (isEdit) {
      update.mutate({ name, description: description || undefined, playbook, schedule, target_type: targetType, target_id: targetId, awx_limit: awxLimit }, {
        onSuccess: onClose,
      })
    } else {
      const req: CreateScheduledTaskRequest = {
        name, description: description || undefined, playbook, schedule,
        target_type: targetType, target_id: targetId, awx_limit: awxLimit,
      }
      create.mutate(req, { onSuccess: onClose })
    }
  }

  return (
    <div
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(0,0,0,0.6)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={e => { if (e.target === e.currentTarget) onClose() }}
    >
      <form
        onSubmit={handleSubmit}
        style={{
          background: 'var(--color-surface)',
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius)',
          padding: 28,
          width: 520,
          maxWidth: '90vw',
        }}
      >
        <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 20 }}>
          {isEdit ? 'Edit scheduled task' : 'New scheduled task'}
        </h2>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={{ fontSize: 11, color: 'var(--color-text-muted)', display: 'block', marginBottom: 4 }}>
              Task name <span style={{ color: 'var(--color-danger)' }}>*</span>
            </label>
            <input value={name} onChange={e => setName(e.target.value)} required style={inputStyle} placeholder="e.g. nightly-update-check" />
          </div>

          <div style={{ gridColumn: '1 / -1' }}>
            <label style={{ fontSize: 11, color: 'var(--color-text-muted)', display: 'block', marginBottom: 4 }}>Description</label>
            <input value={description} onChange={e => setDescription(e.target.value)} style={inputStyle} placeholder="Optional description" />
          </div>

          <div>
            <label style={{ fontSize: 11, color: 'var(--color-text-muted)', display: 'block', marginBottom: 4 }}>
              Playbook <span style={{ color: 'var(--color-danger)' }}>*</span>
            </label>
            {playbooks.length > 0 ? (
              <select value={playbook} onChange={e => setPlaybook(e.target.value)} required style={{ ...inputStyle, cursor: 'pointer' }}>
                <option value="">— Select playbook —</option>
                {playbooks.map(p => <option key={p.name} value={p.name}>{p.name}</option>)}
              </select>
            ) : (
              <input value={playbook} onChange={e => setPlaybook(e.target.value)} required style={inputStyle} placeholder="AWX job template name" />
            )}
          </div>

          <div>
            <label style={{ fontSize: 11, color: 'var(--color-text-muted)', display: 'block', marginBottom: 4 }}>
              Schedule (cron) <span style={{ color: 'var(--color-danger)' }}>*</span>
            </label>
            <input value={schedule} onChange={e => setSchedule(e.target.value)} required style={inputStyle} placeholder="0 3 * * *" />
            {schedule && (
              <div style={{ fontSize: 11, color: 'var(--color-text-muted)', marginTop: 4 }}>
                {cronHuman(schedule)}
                {' — '}
                <span title="Cron is interpreted in the server timezone (SCHEDULER_TIMEZONE env var, default UTC). Set it to Europe/Rome so 11:51 means 11:51 Rome time.">
                  orario locale server ⓘ
                </span>
              </div>
            )}
          </div>

          <div>
            <label style={{ fontSize: 11, color: 'var(--color-text-muted)', display: 'block', marginBottom: 4 }}>Target type</label>
            <select value={targetType} onChange={e => setTargetType(e.target.value as TargetType)} style={{ ...inputStyle, cursor: 'pointer' }}>
              <option value="fleet">Fleet</option>
              <option value="device">Device</option>
            </select>
          </div>

          <div>
            <label style={{ fontSize: 11, color: 'var(--color-text-muted)', display: 'block', marginBottom: 4 }}>
              Target <span style={{ color: 'var(--color-danger)' }}>*</span>
            </label>
            {targetType === 'fleet' ? (
              <select value={targetId} onChange={e => setTargetId(e.target.value)} required style={{ ...inputStyle, cursor: 'pointer' }}>
                <option value="">— Select fleet —</option>
                {fleets.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
              </select>
            ) : (
              <select value={targetId} onChange={e => setTargetId(e.target.value)} required style={{ ...inputStyle, cursor: 'pointer' }}>
                <option value="">— Select device —</option>
                {devices.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            )}
          </div>

          <div style={{ gridColumn: '1 / -1' }}>
            <label style={{ fontSize: 11, color: 'var(--color-text-muted)', display: 'block', marginBottom: 4 }}>
              AWX limit override
              <span style={{ marginLeft: 6, fontWeight: 400 }}>(leave empty to run on all fleet hosts)</span>
            </label>
            <input value={awxLimit} onChange={e => setAwxLimit(e.target.value)} style={inputStyle} placeholder="e.g. device-name-001 or group-name" />
          </div>
        </div>

        {(create.isError || update.isError) && (
          <div style={{ color: 'var(--color-danger)', fontSize: 12, marginTop: 12 }}>
            Failed to save task.
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, marginTop: 20 }}>
          <button type="submit" className="btn btn-primary" disabled={isPending}>
            {isPending ? 'Saving…' : isEdit ? 'Save changes' : 'Create task'}
          </button>
          <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
        </div>
      </form>
    </div>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────

export function ScheduledTasks({ tenantId }: Props) {
  const [showCreate, setShowCreate] = useState(false)
  const [editTask, setEditTask] = useState<ScheduledTask | null>(null)
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null)

  const { data, isLoading, error } = useScheduledTasks(tenantId)
  const { data: fleetsData } = useFleets(tenantId)
  const { data: devicesData } = useDevices(tenantId)
  const { data: playbooksData } = usePlaybooks(tenantId)

  const fleets = fleetsData?.data ?? []
  const devices = devicesData?.data ?? []
  const playbooks = playbooksData ?? []
  const tasks = data?.data ?? []

  const deleteTask = useDeleteScheduledTask(tenantId ?? '')
  const toggleTask = useToggleScheduledTask(tenantId ?? '')

  function fleetName(id: string) {
    return fleets.find(f => f.id === id)?.name ?? id.slice(0, 8) + '…'
  }

  function deviceName(id: string) {
    return devices.find(d => d.id === id)?.name ?? id.slice(0, 8) + '…'
  }

  return (
    <div className="page">
      {(showCreate || editTask) && tenantId && (
        <TaskFormModal
          tenantId={tenantId}
          task={editTask ?? undefined}
          fleets={fleets}
          devices={devices}
          playbooks={playbooks}
          onClose={() => { setShowCreate(false); setEditTask(null) }}
        />
      )}

      <div className="page-header">
        <div>
          <h1 className="page-title">Scheduled Tasks</h1>
          <p className="page-subtitle">{data?.total ?? 0} tasks</p>
        </div>
        <RequirePermission perm="fleets:write">
          <button className="btn btn-primary" onClick={() => setShowCreate(true)}>
            + New task
          </button>
        </RequirePermission>
      </div>

      {isLoading && <div className="empty-state">Loading…</div>}
      {error && <div className="empty-state" style={{ color: 'var(--color-danger)' }}>Error loading tasks.</div>}

      {!isLoading && tasks.length === 0 && (
        <div className="empty-state">
          No scheduled tasks yet. Create one to automatically run playbooks on a schedule.
        </div>
      )}

      {tasks.length > 0 && (
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Playbook</th>
                <th>Schedule</th>
                <th>Target</th>
                <th>Last run</th>
                <th>Next run</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {tasks.map(task => (
                <tr key={task.id} style={{ opacity: task.enabled ? 1 : 0.5 }}>
                  <td style={{ fontWeight: 500 }}>
                    {task.name}
                    {task.description && (
                      <div style={{ fontSize: 11, color: 'var(--color-text-muted)', marginTop: 2 }}>
                        {task.description}
                      </div>
                    )}
                  </td>
                  <td style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>{task.playbook}</td>
                  <td>
                    <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11 }}>{task.schedule}</span>
                    <div style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>
                      {cronHuman(task.schedule)}
                    </div>
                  </td>
                  <td style={{ fontSize: 12 }}>
                    <span style={{ color: 'var(--color-text-muted)', marginRight: 4 }}>{task.target_type}</span>
                    {task.target_type === 'fleet' ? fleetName(task.target_id) : deviceName(task.target_id)}
                  </td>
                  <td style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>
                    {task.last_run_at ? new Date(task.last_run_at).toLocaleString() : '—'}
                  </td>
                  <td style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>
                    {task.next_run_at && task.enabled
                      ? new Date(task.next_run_at).toLocaleString()
                      : '—'}
                  </td>
                  <td>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                      {task.last_run_status
                        ? statusBadge(task.last_run_status)
                        : <span style={{ color: 'var(--color-text-muted)', fontSize: 12 }}>—</span>
                      }
                      {task.last_run_job_id && (
                        <span
                          style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--color-text-muted)', cursor: 'pointer' }}
                          title={`AWX job #${task.last_run_job_id} — click to copy`}
                          onClick={() => navigator.clipboard.writeText(task.last_run_job_id!)}
                        >
                          job #{task.last_run_job_id}
                        </span>
                      )}
                      {task.last_run_error && (
                        <span
                          style={{ fontSize: 10, color: 'var(--color-danger)', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}
                          title={task.last_run_error}
                        >
                          {task.last_run_error}
                        </span>
                      )}
                    </div>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                      {/* Enable / disable toggle */}
                      <RequirePermission perm="fleets:write">
                        <button
                          className="btn btn-ghost"
                          style={{ padding: '4px 10px', fontSize: 12 }}
                          onClick={() => toggleTask.mutate({ taskId: task.id, enabled: !task.enabled })}
                        >
                          {task.enabled ? 'Disable' : 'Enable'}
                        </button>
                      </RequirePermission>

                      <RequirePermission perm="fleets:write">
                        <button
                          className="btn btn-ghost"
                          style={{ padding: '4px 10px', fontSize: 12 }}
                          onClick={() => setEditTask(task)}
                        >
                          Edit
                        </button>
                      </RequirePermission>

                      <RequirePermission perm="fleets:write">
                        {confirmDeleteId === task.id ? (
                          <span style={{ display: 'inline-flex', gap: 4, alignItems: 'center', fontSize: 12 }}>
                            <span style={{ color: 'var(--color-text-muted)' }}>Delete?</span>
                            <button
                              className="btn btn-danger"
                              style={{ padding: '4px 8px', fontSize: 12 }}
                              onClick={() => { deleteTask.mutate(task.id); setConfirmDeleteId(null) }}
                            >
                              Yes
                            </button>
                            <button
                              className="btn btn-ghost"
                              style={{ padding: '4px 8px', fontSize: 12 }}
                              onClick={() => setConfirmDeleteId(null)}
                            >
                              No
                            </button>
                          </span>
                        ) : (
                          <button
                            className="btn btn-danger"
                            style={{ padding: '4px 10px', fontSize: 12 }}
                            onClick={() => setConfirmDeleteId(task.id)}
                          >
                            Delete
                          </button>
                        )}
                      </RequirePermission>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
